// Pure helpers for the per-view matrix data path. Operates on the
// per-entity wire shapes from `@lib/api-types/*` (returned by the
// /views/* endpoints via `useViews.ts`).
//
// Sister to `./data.ts`, which holds the legacy-snapshot variants
// still used by Timeline + Activity until they get their own
// per-view endpoints (PR 3 of the per-entity migration). When that
// lands, `data.ts` retires and these helpers absorb the timeline-
// only pieces (diffSnapshots / mergeSnapshotsForDiff /
// getTimelineSnapshot).
//
// Field-rename rules vs. the legacy helpers:
//   Engineer.display        → display_name
//   Rating.{engineer,skill,rating} → {engineer_id,skill_id,value}
//   Skill.institutional     → derived from `kind === 'institutional'`
//   Engineer.scope === 'gtm' → no longer a category in EngineerScope
//                              (mapped to 'other' on ingest)

import type { BusinessFunction } from '@lib/api-types/business-functions';
import type { DerivedContext, Skill } from '@lib/api-types/derived-contexts';
import type { Engineer } from '@lib/api-types/engineers';
import type { Introduction, Ownership, Rating, RatingValue } from '@lib/api-types/ratings';

export type { Skill, DerivedContext, BusinessFunction, Engineer, Rating, RatingValue };

// ---- Engineer / rating helpers ----

export function isAgent(engineer: Engineer): boolean {
  return engineer.kind === 'agent';
}

/**
 * A "pure helper" agent whose ratings should be capped: an agent that has NEVER
 * authored a commit under its own identity — it earns credit only by
 * co-authoring (a shared assistant like Claude Code). An AUTONOMOUS agent (one
 * that authors under its own identity, e.g. a unit-test bot) has
 * `has_authored_contributions === true` and is rendered UNCAPPED, like a human
 * IC. `has_authored_contributions` is composed server-side from observation
 * provenance; treat a missing flag (legacy payloads) as capped, preserving the
 * prior blanket-cap behaviour until a re-derive populates it.
 */
export function isCappedAgent(engineer: Engineer): boolean {
  return engineer.kind === 'agent' && engineer.has_authored_contributions !== true;
}

/**
 * Engineers used as columns in the resilience / talent matrices.
 * Excludes agents (rendered separately), non-active rosters, and
 * non-engineering scopes (design/product/marketing/ops/other —
 * the legacy 'gtm' bucket folded into 'other').
 */
export function activeHumans(team: Engineer[]): Engineer[] {
  return team.filter(
    (e) => e.kind !== 'agent' && e.status === 'active' && e.scope === 'engineering',
  );
}

/**
 * Renderer-style rating cap for a PURE-HELPER agent (see {@link isCappedAgent}):
 * max Competent (2) on framework/library/service skills, max Apprentice (1) on
 * institutional. Autonomous agents and humans are never passed here.
 */
function capForAgent(rating: RatingValue, skill: Skill | undefined): RatingValue {
  if (!skill) return rating;
  if (skill.kind === 'institutional') {
    return Math.min(rating, 1) as RatingValue;
  }
  return Math.min(rating, 2) as RatingValue;
}

/** engineer_id → skill_id → rating value. */
export type RatingIndex = Map<string, Map<string, RatingValue>>;

export function buildRatingIndex(
  skillsById: Map<string, Skill>,
  team: Engineer[],
  ratings: Rating[],
): RatingIndex {
  const idx: RatingIndex = new Map();
  const teamById = new Map(team.map((e) => [e.id, e]));
  for (const r of ratings) {
    const engineer = teamById.get(r.engineer_id);
    if (!engineer) continue;
    const skill = skillsById.get(r.skill_id);
    const value = isCappedAgent(engineer) ? capForAgent(r.value, skill) : r.value;
    if (!idx.has(r.engineer_id)) idx.set(r.engineer_id, new Map());
    idx.get(r.engineer_id)!.set(r.skill_id, value);
  }
  return idx;
}

export type { Ownership };

/** engineer_id → skill_id → Ownership (companion signal A). */
export type OwnershipIndex = Map<string, Map<string, Ownership>>;

/**
 * Index the view's `ownership` array by engineer + skill. Empty for orgs with no
 * institutional skills (the server emits nothing there), so callers can treat a
 * miss as "no ownership badge".
 */
export function buildOwnershipIndex(ownership: Ownership[]): OwnershipIndex {
  const idx: OwnershipIndex = new Map();
  for (const o of ownership) {
    let bySkill = idx.get(o.engineer_id);
    if (!bySkill) {
      bySkill = new Map();
      idx.set(o.engineer_id, bySkill);
    }
    bySkill.set(o.skill_id, o);
  }
  return idx;
}

/**
 * Short per-cell badge for an ownership entry: "Sole builder · 100%" when this
 * engineer is the only owner, else "Primary builder · N%", where N% is CURRENCY —
 * how current they are on the skill's code as it stands now (it decays as others
 * rewrite it, restored on re-touch). A COMPANION to the rating, never a replacement.
 */
export function ownershipLabel(o: Ownership | undefined): string | null {
  if (!o) return null;
  const pct = Math.round(o.currency * 100);
  return `${o.sole ? 'Sole' : 'Primary'} builder · ${pct}%`;
}

export type { Introduction };

/** engineer_id → skill_id → Introduction (companion signal B). */
export type IntroductionIndex = Map<string, Map<string, Introduction>>;

/** Index the view's `introductions` array by engineer + skill. */
export function buildIntroductionIndex(introductions: Introduction[]): IntroductionIndex {
  const idx: IntroductionIndex = new Map();
  for (const it of introductions) {
    let bySkill = idx.get(it.engineer_id);
    if (!bySkill) {
      bySkill = new Map();
      idx.set(it.engineer_id, bySkill);
    }
    bySkill.set(it.skill_id, it);
  }
  return idx;
}

/**
 * Short per-cell badge for an introduction entry: "Introduced" when this engineer
 * first established the skill, else "Built N module(s)" when they authored new
 * modules. Null when there's nothing to show. A COMPANION to the rating — it
 * surfaces who brought a capability in, which the volume rung can't see.
 */
export function introductionLabel(it: Introduction | undefined): string | null {
  if (!it) return null;
  if (it.introduced_skill) return 'Introduced';
  if (it.modules_introduced > 0) {
    return `Built ${it.modules_introduced} module${it.modules_introduced === 1 ? '' : 's'}`;
  }
  return null;
}

export function indexSkills(comps: DerivedContext[]): Map<string, Skill> {
  const map = new Map<string, Skill>();
  for (const c of comps) {
    for (const s of c.skills) {
      // First-write wins so a skill keeps its metadata even if cross-listed.
      if (!map.has(s.id)) map.set(s.id, s);
    }
  }
  return map;
}

export interface SkillCoverage {
  skill: Skill;
  covered: Array<{ engineer: Engineer; rating: RatingValue }>;
}

export function coverageForCell(
  comp: DerivedContext,
  fn: BusinessFunction,
  threshold: RatingValue,
  ratingIndex: RatingIndex,
  skillFunctions: Map<string, Set<string>>,
  team: Engineer[],
): SkillCoverage[] {
  const result: SkillCoverage[] = [];
  for (const skill of comp.skills) {
    const fns = skillFunctions.get(skill.id);
    if (!fns || !fns.has(fn.id)) continue;
    const covered: SkillCoverage['covered'] = [];
    for (const engineer of team) {
      if (engineer.status !== 'active') continue;
      const rating = ratingIndex.get(engineer.id)?.get(skill.id);
      if (rating != null && rating >= threshold) {
        covered.push({ engineer, rating });
      }
    }
    covered.sort((a, b) => b.rating - a.rating);
    result.push({ skill, covered });
  }
  return result;
}

/**
 * Map each skill to the set of business-function ids the matrix
 * should render it under. The set is the union across every
 * derivedContext that lists the skill (cross-listed skills get the
 * union of their hosts' functions).
 */
export function buildSkillFunctions(
  comps: DerivedContext[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const comp of comps) {
    for (const skill of comp.skills) {
      if (!map.has(skill.id)) map.set(skill.id, new Set());
      const set = map.get(skill.id)!;
      // PREFER THE SKILL'S OWN EDGE. Every skill used to inherit its context's
      // entire function list, so an over-broad context put all of its skills under
      // every BF it touched anywhere: `kubernetes` reached the frontend context via
      // one bundled app's 8 k8s manifests and rendered under Patrol, which has no
      // claim on those files.
      //
      // Falls back to the context's set when the skill carries none — a view
      // cached before the field existed, or an org where the computed intersection
      // never ran. Absent is not the same as empty: an empty array means "this
      // skill meets no business function" and is honoured as such.
      for (const fn of skill.functions ?? comp.functions) set.add(fn);
    }
  }
  return map;
}

/**
 * Map each derivedContext to the set of business-function ids it ITSELF serves —
 * its own curated/cluster-derived `functions` edges, the exact source the
 * platform-map SETUP grid lights its cells from
 * (`comp.functions.includes(fn.id)`).
 *
 * Unlike {@link buildSkillFunctions}, this does NOT union across derivedContexts
 * via shared skills, so it cannot manufacture a (derivedContext × function)
 * intersection the derivedContext doesn't actually have. The Resilience matrix
 * gates its cells on this so it agrees with the setup grid: a shared or
 * language-broadcast skill (e.g. `typescript` observed across many
 * derivedContexts) no longer lights a derivedContext under functions it never
 * declared. Coverage COUNTS inside a genuinely-served cell still include
 * those skills — only the phantom cells disappear.
 */
export function buildDerivedContextFunctions(
  comps: DerivedContext[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const comp of comps) map.set(comp.id, new Set(comp.functions));
  return map;
}

/**
 * Resilience cell gate: does the derivedContext's OWN function set include this
 * function? Pass the flat map or its parent-remapped variant (for grouped
 * columns) — remap it exactly like `skillFunctions` via
 * {@link remapSkillFunctionsToParents}. Empty edges → serves nothing, matching
 * the setup grid's strict `comp.functions.includes(...)`.
 */
export function derivedContextOwnsFunction(
  compId: string,
  fnId: string,
  compFunctions: Map<string, Set<string>>,
): boolean {
  return compFunctions.get(compId)?.has(fnId) ?? false;
}

// ---- Business-function roll-up (the matrix's column axis) --------------
//
// THE ROLL-UP IS THE AXIS, not a view mode. Every matrix used to carry a
// "Group functions" toolbar toggle flipping between {@link rollupColumns} and
// {@link flatColumns}; the flat side drew a column per promoted child and hid
// the parents, which is the same surface listed under several names. The
// promoted, top-level set is what the reconcile calls the org's column count
// (`top_level` in `BfReconcileResult`), so that is what the maps draw and the
// toggle is gone. A child is behind its parent's roll-up, and an unpromoted
// candidate never reaches the SPA at all — the composer filters it out
// (`loadBusinessFunctionsAt`, the promotion flag's only reader).

/** A rendered column in the rolled-up business-function axis. */
export interface RollupColumn {
  fn: BusinessFunction;
  /** `group` = a parent (collapsed roll-up); `child` = revealed under an
   *  expanded parent; `plain` = a standalone function. */
  kind: 'group' | 'child' | 'plain';
  /** Number of child functions — only meaningful for `group`. */
  childCount: number;
}

/**
 * Ordered columns for the rolled-up business-function axis. Each top-level
 * function with children becomes one collapsed `group` column; when its id
 * is in `expanded`, its `child` columns follow it. Functions with no
 * children are `plain`. Input order is preserved; a child whose parent is
 * missing degrades to `plain` (defensive). Pure.
 */
export function rollupColumns(
  functions: BusinessFunction[],
  expanded: ReadonlySet<string>,
): RollupColumn[] {
  const childrenOf = new Map<string, BusinessFunction[]>();
  for (const f of functions) {
    if (f.parent_function_id != null) {
      const arr = childrenOf.get(f.parent_function_id) ?? [];
      arr.push(f);
      childrenOf.set(f.parent_function_id, arr);
    }
  }
  const out: RollupColumn[] = [];
  const seen = new Set<string>();
  for (const f of functions) {
    if (f.parent_function_id != null) continue; // emitted under its parent
    const kids = childrenOf.get(f.id) ?? [];
    if (kids.length === 0) {
      out.push({ fn: f, kind: 'plain', childCount: 0 });
      seen.add(f.id);
      continue;
    }
    out.push({ fn: f, kind: 'group', childCount: kids.length });
    seen.add(f.id);
    for (const c of kids) {
      seen.add(c.id);
      if (expanded.has(f.id)) out.push({ fn: c, kind: 'child', childCount: 0 });
    }
  }
  for (const f of functions) {
    if (!seen.has(f.id)) out.push({ fn: f, kind: 'plain', childCount: 0 });
  }
  return out;
}

/** Stable empty set — pass to {@link rollupColumns} to render every parent
 *  collapsed (no children revealed) while click-to-expand is disabled. */
export const NO_EXPANDED: ReadonlySet<string> = new Set();

/**
 * Flat columns for the business-function axis, HIDING the parent group nodes.
 * A parent has no footprint of its own — all its coverage lives in its children
 * — so with no roll-up it would render as an empty column. Drop any function
 * that is a parent of another (some function nests under it); children +
 * standalone functions render as `plain`. Pure.
 *
 * NOT the live matrix axis any more — see the section header above. The deck
 * export ({@link ../components/DeckMatrix}) still builds on it, because its
 * union axis is derived from `comp.functions` edges directly and knows nothing
 * about the hierarchy.
 */
export function flatColumns(functions: BusinessFunction[]): RollupColumn[] {
  const parentIds = new Set<string>();
  for (const f of functions) {
    if (f.parent_function_id != null) parentIds.add(f.parent_function_id);
  }
  return functions
    .filter((f) => !parentIds.has(f.id))
    .map((f) => ({ fn: f, kind: 'plain' as const, childCount: 0 }));
}

/**
 * Remap a skill→functions adjacency map so every child function id is
 * replaced by its parent's id. A rolled-up group column (keyed on the
 * parent id) then aggregates coverage across the parent + all its
 * children through the existing cell math — no special-casing needed.
 * Pure.
 */
export function remapSkillFunctionsToParents(
  skillFunctions: Map<string, Set<string>>,
  functions: BusinessFunction[],
): Map<string, Set<string>> {
  const parentOf = new Map<string, string>();
  for (const f of functions) {
    if (f.parent_function_id != null) parentOf.set(f.id, f.parent_function_id);
  }
  const out = new Map<string, Set<string>>();
  for (const [skillId, fnSet] of skillFunctions) {
    const remapped = new Set<string>();
    for (const fnId of fnSet) remapped.add(parentOf.get(fnId) ?? fnId);
    out.set(skillId, remapped);
  }
  return out;
}

/**
 * The platform map's derivedContext × function intersection gate — the SINGLE
 * source of truth for whether a (derivedContext, function-column) cell exists on
 * ANY platform-map matrix (Resilience, Talent Profile, Timeline, …).
 *
 * A cell exists only when the derivedContext ITSELF serves the function (its own
 * curated / cluster-derived `functions` edges — {@link buildDerivedContextFunctions}),
 * NOT via the per-skill union, so a shared or observed skill can't manufacture a
 * phantom intersection. Handles the rolled-up parent column by remapping child
 * edges onto their parent, so a group column lights when the derivedContext
 * serves the parent or any of its children.
 *
 * Build it once per view (memoize on derivedContexts / functions) and gate every
 * cell with `gate.serves(comp.id, col)`. Changing how derivedContexts and
 * functions intersect anywhere on the platform map is a change to THIS one
 * function — every map follows. (Roadmap's what-if grids extend it with
 * project-assigned "net-new" edges via `req-key.ts`, still built on the same
 * {@link derivedContextOwnsFunction} core.)
 *
 * WHICH MAP IS PICKED PER COLUMN, not per view. This used to take a `groupFns`
 * flag that suppressed the remap wholesale, which made "grouped columns with a
 * flat gate" expressible — and that combination is simply wrong: a `group`
 * column would be gated on the parent's own (always empty) edges and render as a
 * dead column. `col.kind` already says which map a column needs, so the flag
 * could only ever disagree with it.
 */
export interface DerivedContextFunctionGate {
  serves(compId: string, col: RollupColumn): boolean;
}

export function buildDerivedContextFunctionGate(
  derivedContexts: DerivedContext[],
  functions: BusinessFunction[],
): DerivedContextFunctionGate {
  const flat = buildDerivedContextFunctions(derivedContexts);
  const grouped = remapSkillFunctionsToParents(flat, functions);
  return {
    serves(compId, col) {
      // Matches `mapForColumn`: group columns use the parent-remapped edges.
      const map = col.kind === 'group' ? grouped : flat;
      return derivedContextOwnsFunction(compId, col.fn.id, map);
    },
  };
}

/**
 * Whether a derivedContext "serves" a business function — i.e. whether the
 * (derivedContext × function) cell exists on the platform map. True if any of
 * the derivedContext's skills maps to the function (the skill-union footprint,
 * the same source the Resilience map uses), so a derivedContext spans every
 * function its skills reach — not just its own narrow `functions_json`. A
 * derivedContext that declares no functions yet (a freshly-added overlay comp)
 * serves all functions.
 */
export function compServesFunction(
  comp: { functions: string[]; skills: Array<{ id: string }> },
  fnId: string,
  skillFunctions: Map<string, Set<string>>,
): boolean {
  if (comp.functions.length === 0) return true;
  return comp.skills.some((s) => skillFunctions.get(s.id)?.has(fnId));
}

// ---- Display constants (identical to data.ts) ---------------------------
//
// Re-declared rather than re-exported so a future delete of data.ts
// in PR 3 doesn't have to chase a broken re-export. The constant
// values are stable matrix vocabulary that lives with whichever
// module owns the per-view helpers.

export const TALENT_LEVELS: Array<{
  key: RatingValue;
  label: string;
  desc: string;
}> = [
  {
    key: 1,
    label: 'Apprentice +',
    desc: 'Counts anyone with any level of familiarity.',
  },
  {
    key: 2,
    label: 'Competent +',
    desc: 'Counts engineers who can ship without supervision.',
  },
  {
    key: 3,
    label: 'Proficient +',
    desc: 'Counts engineers who can pair-review and lead small projects.',
  },
  {
    key: 4,
    label: 'Expert only',
    desc: 'Counts only engineers who can design from scratch.',
  },
];

export const RATING_LABELS: Record<RatingValue, string> = {
  1: 'Apprentice',
  2: 'Competent',
  3: 'Proficient',
  4: 'Expert',
};

/** Map a covered-engineer count to the CSS level class used by `.snap-cell`. */
export function levelClass(value: number | null): string {
  if (value === null) return 'na';
  if (value === 0) return 'v0';
  return 'lvl-' + Math.min(5, value);
}

/**
 * Total ROOMY-layout matrix height (px) above which the grouped rows switch to
 * the compact regime, so a dense matrix stays scannable without excessive
 * vertical scrolling. ~800px is roughly one laptop viewport of rows; validated
 * against prod so the only org it reclassifies vs the previous unique-skill
 * rule is the one the rule was mis-sizing (Vantage: 1180px roomy).
 */
export const COMPACT_ROWS_HEIGHT_BUDGET_PX = 800;

/**
 * Whether a catalog is dense enough to warrant compact grouped rows.
 *
 * Sums the height each derivedContext row would occupy in the ROOMY layout — the
 * CUMULATIVE measure that actually determines whether the matrix scrolls. An
 * earlier version counted DISTINCT non-library skills, but that undercounts
 * density whenever a skill recurs across derivedContexts (e.g. a base language
 * broadcast onto every derivedContext's row): a catalog with few *unique* skills
 * but many *per-row* skills read as "small" and stayed roomy while overflowing
 * the viewport. Row heights are driven by each comp's own skill count, so the
 * layout decision must be too. Libraries are excluded so the regime can't flip
 * with the "Show Libraries" toggle.
 */
export function isCompactCatalog(
  comps: readonly { skills: readonly { id: string; kind?: string }[] }[],
): boolean {
  let roomyPx = 0;
  for (const c of comps) {
    const visibleSkills = c.skills.reduce((n, s) => (s.kind !== 'library' ? n + 1 : n), 0);
    roomyPx += compRowHeight(visibleSkills, false);
  }
  return roomyPx > COMPACT_ROWS_HEIGHT_BUDGET_PX;
}

/**
 * Height of a derivedContext's single row when the matrix renders in
 * grouped mode. Comps with 1-3 visible skills get the base row height;
 * each additional skill adds a per-skill increment so high-surface
 * comps read visually heavier than narrow ones. Compact catalogs (see
 * `isCompactCatalog`) use tighter values (25px base, +5px/skill) than
 * the roomier default (40px base, +10px/skill).
 */
export function compRowHeight(skillCount: number, compact: boolean): number {
  const base = compact ? 25 : 40;
  const perSkill = compact ? 5 : 10;
  return base + Math.max(0, skillCount - 3) * perSkill;
}

/**
 * Context count at or above which the ULTRA-COMPACT toggle is offered.
 *
 * Below this the roomy/compact ladder already fits a viewport and the control
 * would be noise; at 15+ contexts the grid starts running past the fold no
 * matter which regime `isCompactCatalog` picks (Homebase, at 30 contexts,
 * needs 1304px of rows in ~570px of viewport). This gates only whether the
 * TOGGLE APPEARS — it never turns ultra-compact on by itself.
 */
export const ULTRA_COMPACT_MIN_CONTEXTS = 15;

/** Whether a catalog is big enough to offer the ultra-compact toggle. */
export function canUltraCompact(comps: readonly unknown[]): boolean {
  return comps.length >= ULTRA_COMPACT_MIN_CONTEXTS;
}

/**
 * Ultra-compact grouped row height: FLAT bands, not a per-skill ramp.
 *
 * S (<8 skills) 12px · M (<12) 12px · L (12+) 34px. Flat bands are what make
 * the regime predictable at 30 rows — the compact ramp's problem was that 69%
 * of its height was per-row base, so trimming the increment bought nothing
 * (see docs/features/matrix-row-height-homebase.md).
 *
 * S and M are deliberately the same 12px right now, so the regime reads as
 * "one thin band, plus a taller one for genuinely broad contexts". The three
 * buckets are kept distinct rather than collapsed to two because the split
 * point is still being tuned — only the constants move, not the shape.
 *
 * `totalSkillCount` is the context's WHOLE skill list, libraries included —
 * deliberately NOT the tech-filtered count every other sizing path uses. That
 * is what makes the layout invariant under "Show Libraries": flipping the
 * toggle changes the skill COUNT a row displays but never re-flows the grid,
 * so the rows don't jump under the cursor. Callers must pass the unfiltered
 * length; passing the filtered one silently reintroduces the reflow.
 */
export function ultraCompactRowHeight(totalSkillCount: number): number {
  if (totalSkillCount < 8) return 12;
  if (totalSkillCount < 12) return 12;
  return 34;
}

/**
 * Skills shown by default in the matrix views: frameworks, services,
 * institutional knowledge. Libraries are hidden until "Show libraries"
 * is toggled on.
 */
export function isDefaultVisibleSkill(skill: Skill): boolean {
  return (
    skill.kind === 'framework' ||
    skill.kind === 'service' ||
    skill.kind === 'institutional'
  );
}

/** True when the skill is institutional knowledge. */
export function isInstitutional(skill: Skill): boolean {
  return skill.kind === 'institutional';
}

// ---- Timeline diff helpers ---------------------------------------------
//
// Set-diff between two adjacent Timeline snapshots — drives the
// new/deprecated visual indicators in the matrix. Pure functions
// over `(derivedContexts, business_functions)` shape that match what
// TimelineView consumes.

export interface SnapshotDiff {
  newCompIds: Set<string>;
  removedCompIds: Set<string>;
  newSkillIds: Set<string>;
  removedSkillIds: Set<string>;
  newFnIds: Set<string>;
  removedFnIds: Set<string>;
}

const EMPTY_DIFF: SnapshotDiff = {
  newCompIds: new Set(),
  removedCompIds: new Set(),
  newSkillIds: new Set(),
  removedSkillIds: new Set(),
  newFnIds: new Set(),
  removedFnIds: new Set(),
};

export interface SnapshotLike {
  derivedContexts: DerivedContext[];
  business_functions: BusinessFunction[];
}

export function diffSnapshots(
  current: SnapshotLike,
  prior: SnapshotLike | null,
): SnapshotDiff {
  if (!prior) return EMPTY_DIFF;
  const currentCompIds = new Set(current.derivedContexts.map((c) => c.id));
  const priorCompIds = new Set(prior.derivedContexts.map((c) => c.id));
  const currentSkillIds = new Set(
    current.derivedContexts.flatMap((c) => c.skills.map((s) => s.id)),
  );
  const priorSkillIds = new Set(
    prior.derivedContexts.flatMap((c) => c.skills.map((s) => s.id)),
  );
  const currentFnIds = new Set(current.business_functions.map((f) => f.id));
  const priorFnIds = new Set(prior.business_functions.map((f) => f.id));
  return {
    newCompIds: setDiff(currentCompIds, priorCompIds),
    removedCompIds: setDiff(priorCompIds, currentCompIds),
    newSkillIds: setDiff(currentSkillIds, priorSkillIds),
    removedSkillIds: setDiff(priorSkillIds, currentSkillIds),
    newFnIds: setDiff(currentFnIds, priorFnIds),
    removedFnIds: setDiff(priorFnIds, currentFnIds),
  };
}

function setDiff<T>(a: Set<T>, b: Set<T>): Set<T> {
  const out = new Set<T>();
  for (const v of a) if (!b.has(v)) out.add(v);
  return out;
}

/**
 * Merge current + prior so removed items stay visible (rendered
 * with strikethrough + diagonal-stripe cells). Current ordering is
 * preserved; removed items are appended at the end of their parent
 * list.
 */
export function mergeSnapshotsForDiff(
  current: SnapshotLike,
  prior: SnapshotLike | null,
): SnapshotLike {
  if (!prior) return current;
  const currentCompById = new Map(current.derivedContexts.map((c) => [c.id, c]));
  const mergedComps: DerivedContext[] = current.derivedContexts.map((curr) => {
    const priorComp = prior.derivedContexts.find((p) => p.id === curr.id);
    if (!priorComp) return curr;
    const currentSkillIds = new Set(curr.skills.map((s) => s.id));
    const removedSkills = priorComp.skills.filter((s) => !currentSkillIds.has(s.id));
    if (removedSkills.length === 0) return curr;
    return { ...curr, skills: [...curr.skills, ...removedSkills] };
  });
  for (const priorComp of prior.derivedContexts) {
    if (!currentCompById.has(priorComp.id)) mergedComps.push(priorComp);
  }
  const currentFnIds = new Set(current.business_functions.map((f) => f.id));
  const mergedFns: BusinessFunction[] = [
    ...current.business_functions,
    ...prior.business_functions.filter((f) => !currentFnIds.has(f.id)),
  ];
  return { derivedContexts: mergedComps, business_functions: mergedFns };
}
